import { Button, IconButton, Typography } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import CardSpacer from "@saleor/components/CardSpacer";
import CardTitle from "@saleor/components/CardTitle";
import { ControlledCheckbox } from "@saleor/components/ControlledCheckbox";
import FormSpacer from "@saleor/components/FormSpacer";
import Grid from "@saleor/components/Grid";
import SingleAutocompleteSelectField from "@saleor/components/SingleAutocompleteSelectField";
import { MallErrorFragment } from "@saleor/fragments/types/MallErrorFragment";
import { FormChange } from "@saleor/hooks/useForm";
import useNotifier from "@saleor/hooks/useNotifier";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import useUser from "@saleor/hooks/useUser";
import { commonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { getCityChoices, getProvinceChoices } from "@saleor/staff/utils";
import { PermissionEnum } from "@saleor/types/globalTypes";
import { getFormErrors } from "@saleor/utils/errors";
import getMallErrorMessage from "@saleor/utils/errors/mall";
import {
  formatMobileNumber,
  formatTelephoneNumber
} from "@saleor/utils/formatters/formatPhoneNumber";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import React from "react";
import { useIntl } from "react-intl";

import { ErrorValidation } from "../MallCreatePage";
import MallDeliveryTimeDialog from "../MallDeliveryTimeDialog";

export interface MallInfoProps {
  /** Data */
  data: {
    id: string;
    name: string;
    code: string;
    longitude: string;
    latitude: string;
    totalLandArea: string;
    contactPerson: string;
    telephoneNo: string;
    mobileNo: string;
    email: string;
    mallOpening: string;
    mallClosing: string;
    serviceArea: string;

    /** Status flags */
    isActive: boolean;
    isServiceable: boolean;
    expressDeliveryAvailable: boolean;
    expressLongDistanceAvailable: boolean;
    nextDayAvailable: boolean;
    sameDayAvailable: boolean;
    isNationwideDelivery: boolean;
    isInMallPickup: boolean;

    /** Cutoff times */
    genMerchCutoffTime: string;
    groceryCutoffTime: string;
    foodAndBevCutoffTime: string;
    sameDayGmCutoffTime: string;

    /** External system refs */
    smMarketExternalId: number;
    smMarketExternalName: string;
    smMarketExternalCode: string;

    /** Location */
    city: string;
    provinceCode: string;
    provinceName: string;

    /** Shipping */
    mallShippingMethod: any;
  };

  /** Form State / Flags */
  disabled: boolean;
  errors: MallErrorFragment[];
  clientErrors: ErrorValidation;
  hasManageMallPermission?: boolean;

  /** State setters */
  setOpenHistoryDialog?: React.Dispatch<React.SetStateAction<boolean>>;

  /** Callbacks */
  onChange: FormChange;
  onDeactivate?: () => void;
  onRefetchMall?: () => void;
}

const useStyles = makeStyles(
  theme => ({
    card: {
      overflow: "visible"
    },
    cardContentDisabled: {
      opacity: "0.6",
      pointerEvents: "none"
    },
    cardTitle: {
      "& span": {
        fontWeight: "bold"
      }
    },
    checkboxes: {
      display: "flex",
      flexDirection: "column"
    },
    divSpaceBetween: {
      display: "flex",
      justifyContent: "space-between"
    },
    historyLinkLabel: {
      "& span": {
        fontWeight: "normal"
      },
      background: "none",
      border: "none",
      color: "blue",
      cursor: "pointer",
      padding: 0,
      textDecoration: "underline"
    },
    historyLinkLabelText: {
      fontWeight: "normal",
      textTransform: "none"
    },
    root: {
      overflow: "visible"
    },
    sectionTitle: {
      fontWeight: 600 as 600,
      marginBottom: theme.spacing(),
      marginTop: theme.spacing(2)
    },
    textField: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1),
      width: 200
    },
    textFieldGrid: {
      display: "grid",
      gridColumnGap: theme.spacing(2),
      gridTemplateColumns: "1fr 1fr"
    },
    titleField: {
      margin: theme.spacing(1),
      width: 200
    },
    titleFieldGrid: {
      display: "grid",
      gridRowGap: theme.spacing(1)
    }
  }),
  { name: "MallAddMember" }
);

const MallInfo: React.FC<MallInfoProps> = ({
  data: formData,
  disabled,
  errors,
  clientErrors,
  hasManageMallPermission,
  setOpenHistoryDialog,
  onChange,
  onDeactivate,
  onRefetchMall
}) => {
  const intl = useIntl();
  const classes = useStyles({});
  const formFields = [
    "name",
    "code",
    "longitude",
    "latitude",
    "serviceArea",
    "totalLandArea",
    "contactPerson",
    "email",
    "telephoneNo",
    "isActive",
    "isServiceable",
    "mallOpening",
    "mallClosing",
    "sameDayGmCutoffTime",
    "city",
    "provinceCode"
  ];
  const checkZeroDecimal = numbers => {
    const key = numbers?.split(".").slice(1, 2);

    return key?.toString() === "000";
  };

  const formErrors = getFormErrors(formFields, errors);
  const notify = useNotifier();
  const user = useUser();
  const hasManageOrderTimeframePermission = user.user.userPermissions.some(
    permission => permission.code === PermissionEnum.MANAGE_ORDER_TIMEFRAME
  );

  const [openDelTimeDialog, setOpenDelTimeDialog] = React.useState(false);
  const [shippingMethodId, setShippingMethodId] = React.useState("");
  const [cutOffTime, setCutOffTime] = React.useState("");
  const [defaultStartTime, setDefaultStartTime] = React.useState("");
  const [defaultEndTime, setDefaultEndTime] = React.useState("");

  const [selectedProvince, setSelectedProvince] = React.useState("");
  const [selectedProvinceName, setSelectedProvinceName] = React.useState("");
  const [selectedCity, setSelectedCity] = React.useState("");

  const [provinceChoices, setProvinceChoices] = React.useState([]);
  const [cityChoices, setCityChoices] = React.useState([]);

  const [provinceDisplayValue, setProvinceDisplayValue] = useStateFromProps(
    maybe(() => formData.provinceCode, "")
  );

  React.useEffect(() => {
    if (formData.mallOpening && formData.mallClosing) {
      const mallOpeningTime = Date.parse(`01 Jan 1970 ${formData.mallOpening}`);
      const mallClosingTime = Date.parse(`01 Jan 1970 ${formData.mallClosing}`);
      if (mallOpeningTime > mallClosingTime) {
        notify({
          status: "info",
          text: intl.formatMessage({
            defaultMessage:
              "Mall Closing time should be greater than Mall Opening time"
          })
        });
        onChange({
          target: {
            name: "mallOpening",
            value: ""
          }
        });
        onChange({
          target: {
            name: "mallClosing",
            value: ""
          }
        });
      }
    }
  }, [formData.mallOpening, formData.mallClosing]);

  React.useEffect(() => {
    setProvinceChoices(getProvinceChoices());
  }, []);

  const onProvinceChange = createSingleAutocompleteSelectHandler(
    onChange,
    selectedValue => {
      setProvinceDisplayValue(selectedValue);
      setSelectedProvinceName(selectedValue);

      const provinceObject = provinceChoices.find(
        pc => pc.label === selectedValue
      );
      setSelectedProvince(provinceObject?.value);
    },
    provinceChoices
  );

  React.useEffect(() => {
    setCityChoices(getCityChoices(selectedProvince));
  }, [selectedProvince]);

  const onCityChange = createSingleAutocompleteSelectHandler(
    onChange,
    selectedValue => {
      setSelectedCity(selectedValue);
    },
    cityChoices
  );

  React.useEffect(() => {
    if (selectedProvinceName) {
      formData.provinceName = selectedProvinceName;
    }
  }, [selectedProvinceName]);

  React.useEffect(() => {
    if (formData?.provinceCode && !selectedProvince) {
      setSelectedProvince(formData.provinceCode);
    }
  }, [formData?.provinceCode]);

  React.useEffect(() => {
    if (formData?.provinceCode && formData?.city && !selectedCity) {
      setSelectedCity(formData.city);
    }
  }, [formData?.city]);

  return (
    <>
      <CardContent>
        <Grid>
          <div>
            <Card className={classes.root}>
              <CardTitle
                className={classes.cardTitle}
                title={intl.formatMessage(commonMessages.generalInformations)}
              />
              <CardContent
                className={
                  hasManageMallPermission ? "" : classes.cardContentDisabled
                }
              >
                <div className={classes.textFieldGrid}>
                  <TextField
                    id="mallNameTxt"
                    error={!!formErrors.name || !!clientErrors.name}
                    helperText={
                      getMallErrorMessage(formErrors.name, intl) ||
                      clientErrors.name
                    }
                    label={intl.formatMessage({
                      defaultMessage: "Mall Name",
                      description: "text field label"
                    })}
                    name="name"
                    type="text"
                    onChange={onChange}
                    value={formData.name || ""}
                    disabled={disabled}
                  />
                  <TextField
                    id="codeTxt"
                    error={!!formErrors.code || !!clientErrors.code}
                    helperText={
                      getMallErrorMessage(formErrors.code, intl) ||
                      clientErrors.code
                    }
                    label={intl.formatMessage({
                      defaultMessage: "Code",
                      description: "text field label"
                    })}
                    name="code"
                    type="text"
                    onChange={onChange}
                    value={formData.code || ""}
                    disabled={disabled}
                  />
                </div>
                <FormSpacer />
                <div className={classes.textFieldGrid}>
                  <TextField
                    id="longitudeTxt"
                    error={!!formErrors.longitude || !!clientErrors.longitude}
                    helperText={
                      getMallErrorMessage(formErrors.longitude, intl) ||
                      clientErrors.longitude
                    }
                    label={intl.formatMessage({
                      defaultMessage: "Longitude",
                      description: "text field label"
                    })}
                    name="longitude"
                    type="number"
                    onChange={onChange}
                    value={formData.longitude || ""}
                    disabled={disabled}
                  />
                  <TextField
                    id="latitudeTxt"
                    error={!!formErrors.latitude || !!clientErrors.latitude}
                    helperText={
                      getMallErrorMessage(formErrors.latitude, intl) ||
                      clientErrors.latitude
                    }
                    label={intl.formatMessage({
                      defaultMessage: "Latitude",
                      description: "text field label"
                    })}
                    name="latitude"
                    type="number"
                    onChange={onChange}
                    value={formData.latitude || ""}
                    disabled={disabled}
                  />
                </div>

                <FormSpacer />
                <div className={classes.textFieldGrid}>
                  <TextField
                    id="serviceAreaTxt"
                    error={
                      !!formErrors.serviceArea || !!clientErrors.serviceArea
                    }
                    helperText={
                      getMallErrorMessage(formErrors.serviceArea, intl) ||
                      clientErrors.serviceArea
                    }
                    label={intl.formatMessage({
                      defaultMessage: "Service Area",
                      description: "text field label"
                    })}
                    name="serviceArea"
                    type="number"
                    onChange={onChange}
                    value={
                      (checkZeroDecimal(formData.serviceArea)
                        ? parseFloat(formData.serviceArea).toFixed(0)
                        : formData.serviceArea) || ""
                    }
                    disabled={disabled}
                  />
                  <TextField
                    id="mallAreaTxt"
                    error={
                      !!formErrors.totalLandArea || !!clientErrors.totalLandArea
                    }
                    helperText={
                      getMallErrorMessage(formErrors.totalLandArea, intl) ||
                      clientErrors.totalLandArea
                    }
                    label={intl.formatMessage({
                      defaultMessage: "Mall Area",
                      description: "text field label"
                    })}
                    name="totalLandArea"
                    type="number"
                    onChange={onChange}
                    value={
                      (checkZeroDecimal(formData.totalLandArea)
                        ? parseFloat(formData.totalLandArea).toFixed(0)
                        : formData.totalLandArea) || ""
                    }
                    disabled={disabled}
                  />
                </div>

                <FormSpacer />
                <div className={classes.textFieldGrid}>
                  <SingleAutocompleteSelectField
                    displayValue={provinceDisplayValue}
                    error={
                      !!formErrors.provinceCode || !!clientErrors.provinceCode
                    }
                    helperText={
                      (!!clientErrors.provinceCode &&
                        clientErrors.provinceCode) ||
                      getMallErrorMessage(formErrors.provinceCode, intl)
                    }
                    label={intl.formatMessage(
                      commonMessages.homeAddressProvince
                    )}
                    name="provinceCode"
                    onChange={e => {
                      formData.city = "";
                      setSelectedCity("");
                      setCityChoices([]);

                      onProvinceChange(e);
                    }}
                    value={selectedProvince}
                    choices={provinceChoices}
                    InputProps={{
                      autoComplete: "off"
                    }}
                    disabled={disabled}
                    checkValueLabel={true}
                  />
                  <SingleAutocompleteSelectField
                    displayValue={selectedCity}
                    error={!!formErrors.city || !!clientErrors.city}
                    helperText={
                      (!!clientErrors.city && clientErrors.city) ||
                      getMallErrorMessage(formErrors.city, intl)
                    }
                    label={intl.formatMessage(commonMessages.homeAddressCity)}
                    name="city"
                    onChange={onCityChange}
                    value={selectedCity}
                    choices={cityChoices}
                    InputProps={{
                      autoComplete: "off"
                    }}
                    disabled={disabled}
                    checkValueLabel={true}
                  />
                </div>
              </CardContent>
            </Card>

            <CardSpacer />
            <Card className={classes.root}>
              <CardTitle
                className={classes.cardTitle}
                title={intl.formatMessage({
                  defaultMessage: "Contact Information",
                  description: "text field label"
                })}
              />
              <CardContent
                className={
                  hasManageMallPermission ? "" : classes.cardContentDisabled
                }
              >
                <div className={classes.textFieldGrid}>
                  <TextField
                    id="contactPersonTxt"
                    error={
                      !!formErrors.contactPerson || !!clientErrors.contactPerson
                    }
                    helperText={
                      getMallErrorMessage(formErrors.contactPerson, intl) ||
                      clientErrors.contactPerson
                    }
                    label={intl.formatMessage({
                      defaultMessage: "Contact Person",
                      description: "text field label"
                    })}
                    name="contactPerson"
                    type="text"
                    onChange={onChange}
                    value={formData.contactPerson || ""}
                    disabled={disabled}
                  />
                  <TextField
                    id="emailTxt"
                    error={!!formErrors.email || !!clientErrors.email}
                    helperText={
                      getMallErrorMessage(formErrors.email, intl) ||
                      clientErrors.email
                    }
                    label={intl.formatMessage({
                      defaultMessage: "Email",
                      description: "text field label"
                    })}
                    name="email"
                    type="text"
                    onChange={onChange}
                    value={formData.email || ""}
                    disabled={disabled}
                  />
                </div>
                <FormSpacer />
                <div className={classes.textFieldGrid}>
                  <TextField
                    id="telephoneNumberTxt"
                    autoComplete="off"
                    error={
                      !!formErrors.telephoneNo || !!clientErrors.telephoneNo
                    }
                    helperText={
                      getMallErrorMessage(formErrors.telephoneNo, intl) ||
                      clientErrors.telephoneNo
                    }
                    label={intl.formatMessage({
                      defaultMessage: "Telephone Number",
                      description: "text field label"
                    })}
                    name="telephoneNo"
                    type="tel"
                    onChange={onChange}
                    value={formatTelephoneNumber(formData.telephoneNo) || ""}
                    disabled={disabled}
                    inputProps={{ maxLength: 14 }} // Area code + 8 digit number + formatting by libphonenumber-js
                  />
                  <TextField
                    id="mobileNumberTxt"
                    autoComplete="off"
                    error={!!formErrors.mobileNo || !!clientErrors.mobileNo}
                    helperText={
                      getMallErrorMessage(formErrors.mobileNo, intl) ||
                      clientErrors.mobileNo
                    }
                    label={intl.formatMessage({
                      defaultMessage: "Mobile Number",
                      description: "text field label"
                    })}
                    name="mobileNo"
                    type="tel"
                    onChange={onChange}
                    value={formatMobileNumber(formData.mobileNo) || ""}
                    disabled={disabled}
                    inputProps={{ maxLength: 11 }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
          <div>
            <Card>
              <CardTitle
                className={classes.cardTitle}
                title={intl.formatMessage({
                  defaultMessage: "Status",
                  description: "Status of Mall"
                })}
              />
              <CardContent className={classes.checkboxes}>
                <ControlledCheckbox
                  id="activeCheckbox"
                  name="isActive"
                  label={intl.formatMessage({
                    defaultMessage: "Active",
                    description: "switch button"
                  })}
                  checked={formData.isActive}
                  onChange={
                    formData.isActive && onDeactivate ? onDeactivate : onChange
                  }
                  disabled={disabled}
                />
                <ControlledCheckbox
                  id="serviceableCheckbox"
                  name="isServiceable"
                  label={intl.formatMessage({
                    defaultMessage: "Serviceable",
                    description: "switch button"
                  })}
                  checked={formData.isServiceable}
                  onChange={onChange}
                  disabled={!formData.isActive ? true : false}
                />
              </CardContent>
            </Card>
            <CardSpacer />
            <Card className={classes.cardTitle}>
              <CardTitle
                title={intl.formatMessage({
                  defaultMessage: "Operating Hours",
                  description: "Operating Hours"
                })}
              />
              <CardContent
                className={
                  hasManageMallPermission ? "" : classes.cardContentDisabled
                }
              >
                <div className={classes.textFieldGrid}>
                  <TextField
                    id="openingTxt"
                    label={intl.formatMessage({
                      defaultMessage: "Opening",
                      description: "switch button"
                    })}
                    type="time"
                    name="mallOpening"
                    onChange={onChange}
                    value={formData.mallOpening || ""}
                    className={classes.textField}
                    InputLabelProps={{
                      shrink: true
                    }}
                    inputProps={{
                      step: 300 // 5 min
                    }}
                    disabled={disabled}
                    error={
                      !!formErrors.mallOpening || !!clientErrors.mallOpening
                    }
                    helperText={
                      getMallErrorMessage(formErrors.mallOpening, intl) ||
                      clientErrors.mallOpening
                    }
                  />
                  <TextField
                    id="closingTxt"
                    label={intl.formatMessage({
                      defaultMessage: "Closing",
                      description: "switch button"
                    })}
                    type="time"
                    name="mallClosing"
                    onChange={onChange}
                    value={formData.mallClosing || ""}
                    className={classes.textField}
                    InputLabelProps={{
                      shrink: true
                    }}
                    inputProps={{
                      step: 300 // 5 min
                    }}
                    disabled={disabled}
                    error={
                      !!formErrors.mallClosing || !!clientErrors.mallClosing
                    }
                    helperText={
                      getMallErrorMessage(formErrors.mallClosing, intl) ||
                      clientErrors.mallClosing
                    }
                  />
                </div>
              </CardContent>
            </Card>

            <CardSpacer />
            <Card>
              <CardTitle
                className={classes.cardTitle}
                title={intl.formatMessage({ defaultMessage: "Delivery Type" })}
                toolbar={
                  formData?.id &&
                  setOpenHistoryDialog && (
                    <Button
                      onClick={() => setOpenHistoryDialog(true)}
                      color="primary"
                      variant="text"
                      className={classes.historyLinkLabel}
                    >
                      <span className={classes.historyLinkLabelText}>
                        View History Log
                      </span>
                    </Button>
                  )
                }
              />
              <CardContent>
                <div>
                  <ControlledCheckbox
                    id="expressDeliveryCheckbox"
                    name="nextDayAvailable"
                    label={intl.formatMessage({
                      defaultMessage: "Standard Delivery",
                      description: "switch button"
                    })}
                    checked={formData.nextDayAvailable}
                    onChange={onChange}
                    disabled={disabled}
                  />
                </div>
                <div>
                  <ControlledCheckbox
                    id="inStorePickupCheckbox"
                    name="isInMallPickup"
                    label={intl.formatMessage({
                      defaultMessage: "In-Store Pickup",
                      description: "switch button"
                    })}
                    checked={formData.isInMallPickup}
                    onChange={onChange}
                    disabled={disabled}
                  />
                </div>
                <div className={classes.divSpaceBetween}>
                  <ControlledCheckbox
                    name="sameDayAvailable"
                    id="sameDayDeliveryCheckbox"
                    label={intl.formatMessage({
                      defaultMessage: "Same Day Delivery",
                      description: "switch button"
                    })}
                    checked={formData.sameDayAvailable}
                    onChange={onChange}
                    disabled={disabled}
                  />
                  {formData?.id && hasManageOrderTimeframePermission && (
                    <IconButton
                      onClick={() => {
                        const shippingMethodName = "Same Day Delivery";
                        const sddItem = formData?.mallShippingMethod?.find(
                          item =>
                            item.shippingMethod.name === shippingMethodName
                        );
                        if (sddItem?.shippingMethod?.id) {
                          setShippingMethodId(sddItem?.shippingMethod?.id);
                          setDefaultStartTime(sddItem?.startTime);
                          setDefaultEndTime(sddItem?.endTime);
                          setCutOffTime(formData.sameDayGmCutoffTime);
                          setOpenDelTimeDialog(true);
                        } else {
                          notify({
                            status: "error",
                            text: intl.formatMessage({
                              defaultMessage:
                                "Cannot find Mall Shipping Method for " +
                                shippingMethodName
                            })
                          });
                        }
                      }}
                    >
                      <EditOutlinedIcon color={"secondary"} />
                    </IconButton>
                  )}
                </div>
                <div>
                  <ControlledCheckbox
                    name="expressDeliveryAvailable"
                    id="expressDeliveryCheckbox"
                    label={intl.formatMessage({
                      defaultMessage: "Express Delivery",
                      description: "switch button"
                    })}
                    checked={formData.expressDeliveryAvailable}
                    onChange={onChange}
                    disabled={disabled}
                  />
                </div>
                <div>
                  <ControlledCheckbox
                    name="expressLongDistanceAvailable"
                    id="expressLongDistanceCheckbox"
                    label={intl.formatMessage({
                      defaultMessage: "Express Long-Distance Delivery",
                      description: "switch button"
                    })}
                    checked={formData.expressLongDistanceAvailable}
                    onChange={onChange}
                    disabled={disabled}
                  />
                </div>
                <div>
                  <ControlledCheckbox
                    name="isNationwideDelivery"
                    id="nationwideDeliveryCheckbox"
                    label={intl.formatMessage({
                      defaultMessage: "Nationwide Delivery",
                      description: "switch button"
                    })}
                    checked={formData.isNationwideDelivery}
                    onChange={onChange}
                    disabled={disabled}
                  />
                </div>
              </CardContent>
            </Card>
            <CardSpacer />
            <Card>
              <CardTitle
                className={classes.cardTitle}
                title={intl.formatMessage({ defaultMessage: "SM Market" })}
              />
              <CardContent
                className={
                  hasManageMallPermission ? "" : classes.cardContentDisabled
                }
              >
                <TextField
                  id="marketNameTxt"
                  error={
                    !!formErrors.smMarketExternalName ||
                    !!clientErrors.smMarketExternalName
                  }
                  disabled={disabled}
                  name="smMarketExternalName"
                  onChange={onChange}
                  label={intl.formatMessage({
                    defaultMessage: "Market Name"
                  })}
                  value={formData.smMarketExternalName || ""}
                  type="text"
                  InputLabelProps={{
                    shrink: true
                  }}
                  fullWidth
                  helperText={
                    getMallErrorMessage(
                      formErrors.smMarketExternalName,
                      intl
                    ) || clientErrors.smMarketExternalName
                  }
                />
                <FormSpacer />
                <TextField
                  id="marketIdTxt"
                  error={
                    !!formErrors.smMarketExternalId ||
                    !!clientErrors.smMarketExternalId
                  }
                  fullWidth
                  disabled={disabled}
                  name="smMarketExternalId"
                  onChange={onChange}
                  label={intl.formatMessage({
                    defaultMessage: "Market ID"
                  })}
                  value={formData.smMarketExternalId || ""}
                  type="number"
                  InputLabelProps={{
                    shrink: true
                  }}
                  onKeyPress={event => {
                    if (!/[0-9]/.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                  helperText={
                    getMallErrorMessage(formErrors.smMarketExternalId, intl) ||
                    clientErrors.smMarketExternalId
                  }
                />
                <FormSpacer />
                <TextField
                  id="externalCodeTxt"
                  error={
                    !!formErrors.smMarketExternalCode ||
                    !!clientErrors.smMarketExternalCode
                  }
                  fullWidth
                  disabled={disabled}
                  name="smMarketExternalCode"
                  onChange={onChange}
                  label={intl.formatMessage({
                    defaultMessage: "External Code"
                  })}
                  value={formData.smMarketExternalCode || ""}
                  type="text"
                  InputLabelProps={{
                    shrink: true
                  }}
                  helperText={
                    getMallErrorMessage(
                      formErrors.smMarketExternalCode,
                      intl
                    ) || clientErrors.smMarketExternalCode
                  }
                />
              </CardContent>
            </Card>
            <CardSpacer />
            <Card>
              <CardTitle
                className={classes.cardTitle}
                title={intl.formatMessage({ defaultMessage: "Cut Off Time" })}
              />
              <CardContent>
                <div className={classes.titleFieldGrid}>
                  <div>
                    <Typography className={classes.titleField}>
                      GENERAL MERCHANDISE
                    </Typography>
                  </div>
                </div>
                <div className={classes.textFieldGrid}>
                  <div>
                    <TextField
                      id="generalMerchandiseCutoffTxt"
                      disabled={disabled}
                      // error={!!clientErrors.mallOpening}
                      // helperText={clientErrors.mallOpening}
                      name="genMerchCutoffTime"
                      onChange={onChange}
                      label={intl.formatMessage({
                        defaultMessage: "Standard Delivery"
                      })}
                      value={formData.genMerchCutoffTime || ""}
                      type="time"
                      className={classes.textField}
                      InputLabelProps={{
                        shrink: true
                      }}
                      fullWidth
                    />
                  </div>
                  <div>
                    {(!formData?.id || hasManageOrderTimeframePermission) && (
                      <TextField
                        id="sameDayDeliveryCutoffTimeTxt"
                        disabled={disabled}
                        name="sameDayGmCutoffTime"
                        onChange={onChange}
                        label={intl.formatMessage({
                          defaultMessage: "Same Day Delivery"
                        })}
                        value={formData.sameDayGmCutoffTime || ""}
                        type="time"
                        className={classes.textField}
                        InputLabelProps={{
                          shrink: true
                        }}
                        fullWidth
                        error={!!formErrors.sameDayGmCutoffTime}
                        helperText={formErrors?.sameDayGmCutoffTime?.message}
                      />
                    )}
                  </div>
                </div>
                <FormSpacer />
                <div className={classes.titleFieldGrid}>
                  <div>
                    <Typography className={classes.titleField}>
                      FOOD & BEVERAGE
                    </Typography>
                  </div>
                </div>
                <div className={classes.textFieldGrid}>
                  <div>
                    <TextField
                      id="groceryCutoffTxt"
                      disabled={disabled}
                      // error={!!clientErrors.mallOpening}
                      // helperText={clientErrors.mallOpening}
                      name="foodAndBevCutoffTime"
                      onChange={onChange}
                      label={intl.formatMessage({
                        defaultMessage: "Express Delivery"
                      })}
                      value={formData.foodAndBevCutoffTime || ""}
                      type="time"
                      className={classes.textField}
                      InputLabelProps={{
                        shrink: true
                      }}
                      fullWidth
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </Grid>
      </CardContent>

      <MallDeliveryTimeDialog
        open={openDelTimeDialog}
        onClose={() => setOpenDelTimeDialog(false)}
        handleConfirm={() => {
          notify({
            status: "success",
            text: intl.formatMessage({
              defaultMessage: "Changes saved!"
            })
          });
          onRefetchMall();
          setOpenDelTimeDialog(false);
        }}
        handleCancel={() => setOpenDelTimeDialog(false)}
        mallId={formData?.id}
        shippingMethodId={shippingMethodId}
        cutOffTime={cutOffTime}
        defaultStartTime={defaultStartTime}
        defaultEndTime={defaultEndTime}
      />
    </>
  );
};

MallInfo.displayName = "MallInfo";
export default MallInfo;
